/* eslint-disable spire/export-styles */
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Hidden from "@insite/mobius/Hidden";
import Icon from "@insite/mobius/Icon";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ChevronsRight from "@sonic/Assets/Icons/ChevronsRight";
import * as cssLinter from "css";
import * as React from "react";
import { FC } from "react";
import { css } from "styled-components";

const enum fields {
    destination = "destination",
    overrideTitle = "overrideTitle",
    customCSS = "customCSS",
    color = "color",
    primary = "primary",
    footerLink = "footerLink",
    headerButton = "headerButton",
    alignment = "alignment",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.destination]: LinkFieldValue;
        [fields.overrideTitle]: string;
        [fields.customCSS]: string;
        [fields.color]: string;
        [fields.primary]: boolean;
        [fields.footerLink]: boolean;
        [fields.headerButton]: boolean;
        [fields.alignment]: string;
    };
}

const linkStyles: InjectableCss = {
    css: css`
        font-size: 21px;
    `,
};

export const Link: FC<OwnProps> = ({ fields }) => {
    const { title, url } = useGetLink(fields.destination);

    const primaryStyles = `
        font-family: Pancetta W05 SemiBold;
        font-size: 21px !important;
        color: #e30613;

        
       
        :hover {
            color: #e30613;
        }
    `;

    const footerLinkStyles = `
        margin-top: 24px;
        
        :hover {
            color: #0679e3; 
        }
    `;

    const buttonStyles = css`
        font-family: "Pancetta W05 SemiBold", sans-serif;
        color: #e30613;
        border: 1px solid #e30613;
        background-color: #fff;
        text-align: center;
        display: flex;
        justify-content: center;
        border-radius: 27px;
        padding: 8px 34px;
        width: 110px;
        margin-right: 16px;
        line-height: 18px;
        font-size: 18px;
    `;

    linkStyles.css = css`
        font-size: 16px;
        color: currentColor;
        transition: color 0.2s ease, text-shadow 0.2s ease;
        text-align: ${fields.alignment};
        display: block;
        &:hover {
            color: rgba(250, 250, 250, 0.6);
        }

        ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(theme, [
                css`
                    ${fields.footerLink
                        ? `
                            text-align: center;
                            width: 100%;
                            display: block;
                            `
                        : `
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        font-size: 18px;
                        padding: 20px 16px;
                        border-top: 2px solid #D8D8D8;

                        &:hover {
                            color: #e30613;
                        }
                    `}
                `,
                css`
                    ${fields.footerLink
                        ? `
                        text-align: center;
                        width: 100%;
                        display: block;
                        `
                        : `
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        font-size: 18px;
                        padding: 20px 16px;
                        border-top: 2px solid #D8D8D8;

                        &:hover {
                            color: #e30613;
                        }
                    `}
                `,
                css`
                    ${fields.footerLink
                        ? ``
                        : `
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        font-size: 18px;
                        padding: 20px 16px;
                        border-top: 2px solid #D8D8D8;

                        &:hover {
                            color: #e30613;
                        }
                    `}
                `,
                null,
                null,
            ])}

        ${fields.primary ? primaryStyles : ""};
        ${fields.footerLink ? footerLinkStyles : ""}
    `;

    fields.headerButton ? (linkStyles.css = buttonStyles) : null;

    if (!url) {
        return null;
    }

    const customCssWrapper = {
        css: css`
            ${fields.customCSS}
        `,
    };

    return (
        <Clickable className={fields.headerButton ? "header-button" : "top-link"} {...linkStyles} href={url}>
            {fields.overrideTitle || title || url}
            {!fields.footerLink && (
                <Hidden above="md">
                    <Icon color={fields.primary ? "#e30613" : "currentColor"} src={ChevronsRight}></Icon>
                </Hidden>
            )}
        </Clickable>
    );
};

const defaultCustomCss = `.link {
}
`;

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: Link,
    definition: {
        group: "Basic",
        icon: "link",
        fieldDefinitions: [
            {
                name: fields.destination,
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                fieldType: "General",
                isRequired: true,
                tab: basicTab,
            },
            {
                name: fields.overrideTitle,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                tab: basicTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
            {
                fieldType: "General",
                name: fields.color,
                displayName: "Text color picker",
                editorTemplate: "ColorPickerField",
                defaultValue: "#fff",
            },
            {
                fieldType: "General",
                name: fields.primary,
                displayName: "Is a Primary Link",
                editorTemplate: "CheckboxField",
                defaultValue: false,
            },
            {
                fieldType: "General",
                name: fields.headerButton,
                displayName: "Display as button",
                editorTemplate: "CheckboxField",
                defaultValue: false,
            },
            {
                fieldType: "General",
                name: fields.footerLink,
                displayName: "Set to true for footer links!",
                editorTemplate: "CheckboxField",
                defaultValue: false,
            },
            {
                fieldType: "General",
                name: fields.alignment,
                displayName: "Text align",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Left", value: "left" },
                    { displayName: "Right", value: "right" },
                    { displayName: "Center", value: "center" },
                ],
                defaultValue: "left",
            },
        ],
    },
};

export default widgetModule;
